import {
  ActionableDataTable,
  ActionableDataTableActionTypes,
} from '../../common/table/ActionableDataTable';
import { humanReadableCriteria } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { DataTableOnOrderChangeClbk } from '../../common/table/TableTypes';
import useCrmOrgPermissions from '../../common/permissions/useCrmOrgPermissions';
import { useSelector } from 'react-redux';
import { useTerritoriesActions } from '../../../hooks/useTerritoriesActions';
import { selectTerritories } from '../../../reducers/territoriesReducer';
import { useRunOnce } from '../../common/useRunOnce';
import { Permission } from '@server/permissions';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { DataTableId } from '../../common/table/utils';
import { SweepCriterion } from '../../../types/SweepCriterion';

interface TieBreakersTableProps {
  tieBreakers: TieBreaker[];
  onTieBreakersChange: (rules: TieBreaker[]) => void;
  onTieBreakerEdit: (ruleId: string) => void;
  onTieBreakerDuplicate: (ruleId: string) => void;
  readonly?: boolean;
}

const columns = [
  {
    field: 'priority',
    headerName: 'Priority',
    width: '78px',
    minWidth: '98px',
  },
  { field: 'name', headerName: 'Name', minWidth: '200px' },
  { field: 'rule', headerName: 'Rule', minWidth: '300px' },
];

interface TieBreakersDataRow {
  id: string;
  priority: string;
  name: string;
  rule: string;
}

export const TieBreakersTable = ({
  tieBreakers,
  onTieBreakersChange,
  onTieBreakerEdit,
  onTieBreakerDuplicate,
  readonly,
}: TieBreakersTableProps) => {
  const permissionString: Permission[] = ['edit:dedupe-matching'];
  const [isAllowedBtn] = useCrmOrgPermissions(permissionString);

  const territories = useSelector(selectTerritories);
  const { getTerritories } = useTerritoriesActions();
  const { enrichCriteriaWithLabelsAndSfdcDisplayType } = useSweepFieldsInfo();

  useRunOnce(async () => {
    await getTerritories();
  });

  const rows: TieBreakersDataRow[] = tieBreakers.map((rule, idx) => ({
    id: rule._id,
    priority: '' + (idx + 1),
    name: rule.name,
    rule: humanReadableCriteria(
      rule.when?.criteria?.length
        ? rule.when.criteria.map((c: SweepCriterion<string>) =>
            enrichCriteriaWithLabelsAndSfdcDisplayType(c),
          )
        : [],
      rule.when?.criteriaLogic,
      territories,
    ),
    // Disable delete action if there's only one rule
    disabledActions: tieBreakers.length === 1 ? [ActionableDataTableActionTypes.DELETE] : undefined,
  }));

  const onOrderChange: DataTableOnOrderChangeClbk = ({ sourceIndex, destinationIndex }) => {
    const newRules = [...tieBreakers];
    newRules.splice(sourceIndex, 1);
    newRules.splice(destinationIndex, 0, tieBreakers[sourceIndex]);
    onTieBreakersChange(newRules);
  };

  const getActions =
    !isAllowedBtn || readonly
      ? []
      : [
          { type: ActionableDataTableActionTypes.EDIT },
          {
            type: ActionableDataTableActionTypes.DUPLICATE,
          },
          {
            type: ActionableDataTableActionTypes.DELETE,
            confirm: true,
            confirmButtonOkLabel: 'Yes, Delete',
            confirmButtonCancelLabel: 'Cancel',
            confirmTitle: 'Delete Rule',
            confirmSubtitle: 'Are you sure you want to delete this rule?',
          },
        ];

  return (
    <ActionableDataTable
      tableId={DataTableId.TieBreakersTable}
      allowRowReorder={isAllowedBtn && !readonly}
      columns={columns}
      rows={rows}
      {...(isAllowedBtn && !readonly ? { onRowOrderChange: onOrderChange } : {})}
      actions={getActions}
      onActionClick={({ actionType, row }) => {
        switch (actionType) {
          case ActionableDataTableActionTypes.DELETE:
            const newRules = tieBreakers.filter((r) => r._id !== row.id);
            onTieBreakersChange(newRules);
            break;
          case ActionableDataTableActionTypes.EDIT:
            onTieBreakerEdit(row.id);
            break;
          case ActionableDataTableActionTypes.DUPLICATE:
            onTieBreakerDuplicate(row.id);
            break;
        }
      }}
      onRowClick={(
        e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
        row: TieBreakersDataRow,
      ) => {
        onTieBreakerEdit(row.id);
      }}
    />
  );
};
